import { Theme } from '@mui/material';
import { Components } from '@mui/material/styles/components';
import type {} from '@mui/x-date-pickers/themeAugmentation';


export const makeOverrides = (): Components<Theme> => ({
  MuiContainer: {
    styleOverrides: {
      maxWidthLg: ({ theme }) => ({
        [theme.breakpoints.up('lg')]: {
          maxWidth: '1248px',
        },
      }),
    }
  },
  MuiSvgIcon: {
    styleOverrides: {
      root: {
        fontSize: '1.5rem',
      },
      fontSizeSmall: {
        fontSize: '1.25rem',
      },
      fontSizeInherit: {
        fontSize: 'inherit',
      },
    }
  },
  MuiListItemButton: {
    styleOverrides: {
      root: {
        '&.Mui-selected': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
          },
        },
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
      }
    }
  },
  MuiLink: {
    styleOverrides: {
      root: ({ theme }) => ({
        textDecorationColor: 'currentColor',
        color: theme.palette.primary.main,
        '&:hover': {
          color: theme.palette.primary.dark
        },
        '&:visited': {
          color: theme.palette.primary.main
        },
      }),
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        cursor: 'inherit',
        fontSize: '0.8125rem',
      }
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        fontWeight: 700,
      }
    }
  },
  MuiTypography: {
    styleOverrides: {
      root: {
        '& strong': {
          fontWeight: 600,
        }
      }
    }
  },
  MuiButtonBase: {
    styleOverrides: {
      root: {
        pointerEvents: 'initial',
      }
    }
  },
  MuiButton: {
    defaultProps: {
      disableRipple: true,
      disableElevation: true,
      color: 'inherit',
    },
    styleOverrides: {
      root: {
        borderRadius: 9999,
        fontWeight: 400,
        '&:hover': {
          textDecoration: 'underline',
        },
      },
      sizeLarge: {
        fontSize: '0.9375rem',
        lineHeight: '1.625rem'
      },
      sizeSmall: {
        fontSize: '0.8125rem',
        lineHeight: '1.375rem'
      },
      colorInherit: {
        '&.MuiButton-outlined': {
          border: '1px solid rgba(0, 0, 0, 0.23)',
        }
      },
      containedPrimary: ({ theme }) => ({
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.dark,
        '&:hover': {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
        },
        '&:active': {
          backgroundColor: theme.palette.primary.dark,
          color: theme.palette.primary.contrastText,
        },
      }),
      containedSecondary: ({ theme }) => ({
        backgroundColor: theme.palette.secondary.light,
        color: theme.palette.secondary.dark,
        '&:hover': {
          backgroundColor: theme.palette.secondary.main,
          color: theme.palette.secondary.contrastText,
        },
        '&:active': {
          backgroundColor: theme.palette.secondary.dark,
          color: theme.palette.secondary.contrastText,
        },
      }),
      containedError: ({ theme }) => ({
        backgroundColor: theme.palette.error.light,
        color: theme.palette.error.dark,
        '&:hover': {
          backgroundColor: theme.palette.error.main,
          color: theme.palette.error.contrastText,
        },
        '&:active': {
          backgroundColor: theme.palette.error.dark,
          color: theme.palette.error.contrastText,
        },
      }),
      outlinedPrimary: ({ theme }) => ({
        '&:hover': {
          backgroundColor: theme.palette.primary.background,
        },
        '&:active': {
          backgroundColor: theme.palette.primary.border,
        },
      }),
      outlinedSecondary: ({ theme }) => ({
        '&:hover': {
          backgroundColor: theme.palette.secondary.background,
        },
        '&:active': {
          backgroundColor: theme.palette.secondary.border,
        },
      }),
      outlinedError: ({ theme }) => ({
        '&:hover': {
          backgroundColor: theme.palette.error.background,
        },
        '&:active': {
          backgroundColor: theme.palette.error.border,
        },
      }),
    }
  },
  MuiStepLabel: {
    styleOverrides: {
      active: ({ theme }) => ({
        color: `${theme.palette.secondary.main} !important`,
        fontWeight: '400 !important' as any,
      }),
      completed: ({ theme }) => ({
        color: `${theme.palette.secondary.main} !important`,
        fontWeight: '400 !important' as any,
      }),
      label: ({ theme }) => ({
        color: theme.palette.text.secondary,
        fontWeight: 400,
      }),
      labelContainer: {
        '& > span': {
          marginTop: '8px !important',
        }
      },
    }
  },
  MuiPickersDay: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: 'transparent',
        '&.Mui-selected': {
          backgroundColor: theme.palette.secondary.main,

          '&:active': {
            backgroundColor: theme.palette.secondary.main,
          },

          '&:hover': {
            backgroundColor: theme.palette.secondary.dark,
          },

          '&:focus': {
            backgroundColor: theme.palette.secondary.main,
          },
        }
      }),
      selected: ({ theme }) => ({
        backgroundColor: theme.palette.secondary.main,

        '&:hover': {
          backgroundColor: theme.palette.secondary.dark,
        }
      }),
      today: ({ theme }) => ({
        color: theme.palette.secondary.main,
      })
    }
  },

  MuiFormLabel: {
    styleOverrides: {
      root: {
        position: 'relative',
        fontSize: '0.875rem',
        lineHeight: '24px',
        transform: 'none',
      },
      asterisk: ({ theme }) => ({
        color: theme.palette.error.main,
      }),
    }
  },

  MuiInputLabel: {
    styleOverrides: {
      root: {
        position: 'relative',
        fontSize: '0.875rem',
        lineHeight: '24px',
        transform: 'none',
      },
      shrink: {
        transform: 'none',
      },
      outlined: {
        transform: 'none !important',
      },
      asterisk: ({ theme }) => ({
        color: theme.palette.error.main,
      }),
    }
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.common.white,

        '& fieldset': {
          transition: theme.transitions.create([ 'border-color', 'box-shadow' ]),
        },

        '& .MuiInputAdornment-root': {
          color: theme.palette.text.secondary,
        },

        '&.Mui-focused fieldset': {
          borderWidth: '1px !important'
        },

        '& legend>span': {
          display: 'none',
        },

        '&.Mui-disabled': {
          background: theme.palette.grey[200],
          color: theme.palette.text.secondary,
        }
      }),
      multiline: {
        paddingTop: '0.5px',
        paddingBottom: '0.5px',
        paddingRight: '0px',
      },
      input: {
        paddingTop: '14px',
        paddingBottom: '14px',
        height: '20px',
        '&:-webkit-autofill': {
          WebkitTextFillColor: 'currentColor',
        },
      },
      notchedOutline: {},
      adornedEnd: {},
    },
  },

  MuiSelect: {
    defaultProps: {
      displayEmpty: true,
    },
    styleOverrides: {
      select: {
        paddingTop: 12,
        paddingBottom: 12,
        '&:focus': {
          backgroundColor: 'inherit',
        },
      },
      icon: {
        backgroundColor: 'rgba(0, 0, 0, 0)',
      },
    }
  },

  MuiAutocomplete: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
          paddingTop: '5px',
          paddingBottom: '5px',
        },
      },

      inputRoot: ({ theme }) => ({
        '& .MuiAutocomplete-endAdornment': {
          right: theme.spacing(1),
        },
      }),
      option: {},
      popper: {},
      paper: {},

      clearIndicator: ({ inputValue }) => ({
        visibility: inputValue ? 'visible' : 'hidden',
      }),

      listbox: ({ theme }) => ({
        maxWidth: '100%',
        overflowX: 'hidden',
        '& .MuiAutocomplete-option[aria-selected="true"]': {
          backgroundColor: theme.palette.grey[50],

          '&.Mui-focused': {
            backgroundColor: theme.palette.grey[100],
          }
        },
      }),

      groupLabel: ({ theme }) => ({
        color: theme.palette.grey[900],
        borderTop: '1px solid',
        borderTopColor: theme.palette.grey[200],
        textTransform: 'uppercase',
        fontSize: '12px',
        fontWeight: 700,

        'li:first-child &': {
          borderTop: 0,
        }
      }),
      endAdornment: {
        right: '8px',
      }
    },
  },

  MuiFormHelperText: {
    styleOverrides: {
      root: {
        display: 'block',
        fontSize: '0.875rem',
        marginLeft: 0,
        marginRight: 0,
      },
    }
  },

  MuiDialogContent: {
    styleOverrides: {
      root: {
        '.MuiDialogTitle-root+&': {
          paddingTop: '24px',
        }
      }
    }
  },
  MuiRadio: {
    defaultProps: {
      color: 'secondary'
    }
  }
  // MuiPickersCalendarHeader: {
  //   styleOverrides: {
  //     iconButton: {
  //       backgroundColor: 'none'
  //     }
  //   }
  // }
});
